import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { SKY_UPGRADE_PATHS } from '../../../packages/domain/src/77-sky-upgrades.mjs';

/** Original modular hard-surface equipment in the pilot's metre-scale GLB bind
 * space. At most six extra meshes draw at once; dormant tiers own bounded GPU
 * resources and every attachment/material/geometry is released with the rider. */
export function createSkyEquipment(T, model) {
  const resources = new Set();
  const mounts = [];
  const own = (resource) => (resources.add(resource), resource);
  const dark = own(
    new T.MeshStandardMaterial({ color: '#283844', metalness: 0.72, roughness: 0.36 }),
  );
  const trim = own(
    new T.MeshStandardMaterial({
      color: '#c5a46a',
      metalness: 0.62,
      roughness: 0.37,
      emissive: '#403019',
      emissiveIntensity: 0.16,
    }),
  );
  let skeleton;
  model.traverse((node) => {
    if (!skeleton && node.isSkinnedMesh) skeleton = node.skeleton;
  });
  const fits = model.userData.skyEquipmentTransforms ?? {};
  const fitting = (name) =>
    new T.Matrix4().fromArray(fits[name] ?? [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  const fitPoint = (name, point) => new T.Vector3(...point).applyMatrix4(fitting(name)).toArray();
  function mount(name) {
    const group = new T.Group();
    group.name = `sky-equipment-${name}`;
    const index = skeleton?.bones.findIndex((bone) => bone.name === name) ?? -1;
    if (index >= 0) {
      group.matrixAutoUpdate = false;
      group.matrix.copy(skeleton.boneInverses[index]).multiply(fitting(name));
      skeleton.bones[index].add(group);
    }
    mounts.push(group);
    return group;
  }
  const weaponMount = mount('forearmR'),
    boosterMount = mount('spine');
  function box(x, y, z, width, height, depth, roll = 0) {
    return new T.BoxGeometry(width, height, depth).rotateZ(roll).translate(x, y, z);
  }
  function cylinder(x, y, z, radius, height, top = radius) {
    return new T.CylinderGeometry(top, radius, height, 12).translate(x, y, z);
  }
  function build(parent, name, parts) {
    const group = new T.Group();
    group.name = name;
    for (const [index, geometries] of parts.entries()) {
      const merged = own(mergeGeometries(geometries, false));
      for (const geometry of geometries) geometry.dispose();
      const mesh = new T.Mesh(merged, index ? trim : dark);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    group.visible = false;
    parent.add(group);
    return group;
  }
  const lengths = [0, 0.12, 0.18, 0.27, 0.36];
  const weapons = Array.from({ length: 4 }, (_, index) => {
    const level = index + 1,
      length = lengths[level],
      radius = 0.045 + level * 0.009;
    const shell = [cylinder(0.61, 0.75 - length / 2, 0.13, radius, length, radius * 0.85)];
    const detail = [cylinder(0.61, 0.76 - length, 0.13, radius * 1.14, 0.035)];
    for (const side of [-1, 1]) {
      shell.push(
        box(0.61 + side * (radius + 0.025), 0.91 - length / 2, 0.13, 0.027, length + 0.31, 0.055),
      );
      for (let band = 0; band < level; band++)
        detail.push(
          box(0.61 + side * (radius + 0.041), 0.92 - band * 0.085, 0.13, 0.012, 0.025, 0.064),
        );
      if (level >= 3) shell.push(box(0.61 + side * 0.12, 1.03, 0.13, 0.05, 0.24, 0.11));
    }
    return build(weaponMount, `sky-weapon-tier-${level}`, [shell, detail]);
  });
  const dualWeapons = lengths.map((length, level) =>
    build(weaponMount, `sky-dual-tier-${level}`, [
      [
        cylinder(0.81, 0.84 - length / 2, 0.13, 0.065, 0.18 + length, 0.055),
        box(0.72, 1.01, 0.13, 0.28, 0.12, 0.14),
        box(0.87, 0.92 - length / 2, 0.13, 0.025, 0.25 + length, 0.05),
      ],
      [
        cylinder(0.81, 0.765 - length, 0.13, 0.074, 0.035),
        box(0.73, 1.08, 0.13, 0.22, 0.025, 0.08),
      ],
    ]),
  );
  const boosters = Array.from({ length: 3 }, (_, index) => {
    const level = index + 1,
      shell = [],
      detail = [];
    for (const side of [-1, 1]) {
      for (let collar = 0; collar < level; collar++) {
        shell.push(cylinder(side * 0.27, 1.55 + collar * 0.15, -0.31, 0.165, 0.045));
        const ring = new T.TorusGeometry(0.166, 0.012, 6, 16)
          .rotateX(Math.PI / 2)
          .translate(side * 0.27, 1.55 + collar * 0.15, -0.31);
        detail.push(ring);
      }
      if (level >= 2) {
        shell.push(box(side * 0.44, 1.84, -0.35, 0.055, 0.38, 0.18, -side * 0.2));
        detail.push(box(side * 0.47, 1.83, -0.46, 0.035, 0.27, 0.024, -side * 0.2));
      }
      if (level === 3) {
        shell.push(box(side * 0.51, 1.99, -0.32, 0.07, 0.32, 0.2, -side * 0.4));
        detail.push(box(side * 0.53, 2.01, -0.43, 0.04, 0.18, 0.025, -side * 0.4));
      }
    }
    return build(boosterMount, `sky-booster-tier-${level}`, [shell, detail]);
  });
  const muzzleBind = fitPoint('forearmR', [0.61, 0.75, 0.13]);
  const dualMuzzleBind = fitPoint('forearmR', [0.81, 0.75, 0.13]);
  const boosterBinds = [-1, 1].map((side) => fitPoint('spine', [side * 0.27, 1.48, -0.31]));
  let lastWeapon = -1,
    lastDual = 0,
    lastBooster = -1,
    disposed = false;
  return {
    muzzleBind,
    dualMuzzleBind,
    boosterBinds,
    update(upgrades = {}) {
      if (disposed) return;
      const level = (key) =>
        Number.isInteger(upgrades?.[key]) &&
        upgrades[key] >= 0 &&
        upgrades[key] <= SKY_UPGRADE_PATHS[key].maxLevel
          ? upgrades[key]
          : 0;
      const weapon = level('weapon'),
        booster = level('booster');
      lastDual = level('dual');
      dualWeapons.forEach((group, index) => {
        group.visible = level('dual') === 1 && index === weapon;
      });
      dualMuzzleBind.splice(0, 3, ...fitPoint('forearmR', [0.81, 0.75 - lengths[weapon], 0.13]));
      if (weapon !== lastWeapon) {
        weapons.forEach((group, index) => {
          group.visible = index + 1 === weapon;
        });
        muzzleBind.splice(0, 3, ...fitPoint('forearmR', [0.61, 0.75 - lengths[weapon], 0.13]));
        lastWeapon = weapon;
      }
      if (booster !== lastBooster) {
        boosters.forEach((group, index) => {
          group.visible = index + 1 === booster;
        });
        lastBooster = booster;
      }
    },
    present(pose, age = 0) {
      // Extendable barrels, collars and vanes retract into the base equipment before
      // a grounded collapse. Their base pack stays part of the skinned body.
      const stow = pose === 'Down' ? T.MathUtils.smoothstep(age, 0.25, 0.8) : 0;
      for (const [groups, dual] of [
        [weapons, false],
        [dualWeapons, true],
      ]) {
        groups.forEach((group, index) => {
          group.scale.setScalar(1 - stow);
          group.position.set(0.61 * stow, stow, 0.13 * stow);
          group.visible =
            (dual ? lastDual === 1 && index === lastWeapon : index + 1 === lastWeapon) && stow < 1;
        });
      }
      boosters.forEach((group, index) => {
        group.scale.setScalar(1 - stow);
        group.position.set(0, 1.8 * stow, -0.31 * stow);
        group.visible = index + 1 === lastBooster && stow < 1;
      });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const group of mounts) group.removeFromParent();
      for (const resource of resources) resource.dispose();
      resources.clear();
    },
  };
}
